import { CartItem } from '../types/shopify';
import { useTranslations } from '../hooks/useTranslations';

/**
 * 複数の商品をShopifyカートに追加します。
 * @param items - カートに追加する商品の配列
 * @returns Shopify Cart APIのレスポンス
 */
export const addItemsToCart = async (items: CartItem[]) => {
  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ items }),
    });

    // レスポンス内容を安全に取得
    let responseData;
    try {
      responseData = await response.json();
    } catch (jsonError) {
      // JSONパースエラー：サーバーがHTMLエラーページを返した場合など
      throw new Error('PARSE_ERROR');
    }

    if (!response.ok) {
      // HTTPエラーの詳細分析
      if (response.status >= 500) {
        throw new Error('SERVER_ERROR'); // サーバーエラー：再試行推奨
      } else if (response.status === 422) {
        throw new Error('VALIDATION_ERROR'); // バリデーションエラー：ユーザー入力問題
      } else if (response.status === 404) {
        throw new Error('PRODUCT_NOT_FOUND'); // 商品が見つからない
      } else {
        throw new Error('CLIENT_ERROR'); // その他のクライアントエラー
      }
    }

    return responseData;
  } catch (error) {
    // ネットワークエラー（接続できない等）
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('NETWORK_ERROR');
    }
    
    // 既に分類済みのエラーはそのまま
    if (error instanceof Error && ['PARSE_ERROR', 'SERVER_ERROR', 'VALIDATION_ERROR', 'PRODUCT_NOT_FOUND', 'CLIENT_ERROR', 'NETWORK_ERROR'].includes(error.message)) {
      throw error;
    }
    
    // 予期しないエラー
    throw new Error('UNKNOWN_ERROR');
  }
};

/**
 * カートAPIエラーをユーザーフレンドリーなメッセージに変換
 * @param error - エラーオブジェクト
 * @param translations - 翻訳オブジェクト
 * @returns ユーザー向けエラーメッセージ
 */
export const getCartErrorMessage = (error: Error, translations: any) => {
  switch (error.message) {
    case 'NETWORK_ERROR':
      return translations?.error_networkIssue || 'インターネット接続を確認してください';
    case 'SERVER_ERROR':
      return translations?.error_serverBusy || 'サーバーが混雑しています。しばらくしてからお試しください';
    case 'VALIDATION_ERROR':
      return translations?.error_invalidData || '入力内容に問題があります。内容を確認してください';
    case 'PRODUCT_NOT_FOUND':
      return translations?.error_productUnavailable || '選択された商品が見つかりません';
    case 'PARSE_ERROR':
      return translations?.error_systemError || 'システムエラーが発生しました。お時間をおいてお試しください';
    case 'CLIENT_ERROR':
      return translations?.error_requestError || 'リクエストに問題があります。もう一度お試しください';
    default:
      return translations?.error_unexpectedError || '予期しないエラーが発生しました';
  }
};

/**
 * エラーが再試行可能かどうかを判定
 * @param error - エラーオブジェクト
 * @returns 再試行可能かどうか
 */
export const isRetryableError = (error: Error): boolean => {
  return ['NETWORK_ERROR', 'SERVER_ERROR', 'PARSE_ERROR'].includes(error.message);
};

/**
 * ファイル情報とその他のオプションをLINE ITEM PROPERTYとして整理します。
 * @param orderData - 注文データ
 * @param uploadedFiles - アップロードされたファイル情報
 * @returns LINE ITEM PROPERTY用のオブジェクト
 */
export const prepareLineItemProperties = (
  orderData: any,
  uploadedFiles: Record<string, File | null>
): Record<string, string> => {
  const t = useTranslations();
  const properties: Record<string, string> = {};

  // 基本的な注文情報
  properties[t.shopify_productType] = orderData.productType;
  if (orderData.beanType) {
    properties[t.shopify_beanType] = orderData.beanType;
  }
  if (orderData.packageType) {
    properties[t.shopify_packageType] = orderData.packageType;
  }
  if (orderData.lotSize) {
    properties[t.shopify_lotSize] = orderData.lotSize.toString();
  }
  if (orderData.sugarConcentration) {
    properties[t.shopify_sugarConcentration] = orderData.sugarConcentration;
  }

  // デザインオプション
  if (orderData.needsInnerDesign) {
    properties[t.shopify_innerDesign] = t.shopify_required;
  }
  if (orderData.needsFrontLabel) {
    properties[t.shopify_frontLabel] = t.shopify_required;
  }
  if (orderData.needsOuterBox) {
    properties[t.shopify_outerBoxRequired] = orderData.needsOuterBoxDesign ? t.shopify_outerBoxWithDesign : t.shopify_outerBoxWithoutDesign;
  }
  if (orderData.needsBackLabel) {
    properties[t.shopify_backLabel] = t.shopify_required;
  }

  // ファイル情報（ファイル名のみ記録、実際のファイルはLiquidフォームで処理）
  const filePropertyNames = {
    innerDesignFile: t.file_innerDesignFile,
    frontLabelFile: t.file_frontLabelFile,
    outerBoxDesignFile: t.file_outerBoxDesignFile,
    backLabelFile: t.file_backLabelFile
  };
  
  Object.entries(uploadedFiles).forEach(([key, file]) => {
    if (file && filePropertyNames[key as keyof typeof filePropertyNames]) {
      properties[filePropertyNames[key as keyof typeof filePropertyNames]] = file.name;
    }
  });

  // 注文識別用
  properties[t.shopify_orderType] = t.shopify_customOrder;
  properties[t.shopify_orderDateTime] = new Date().toISOString();

  return properties;
};

/**
 * ReactからLiquidフォームにデータを渡してカート追加を実行します。
 * この方法により、ファイルアップロードを含む注文をセキュアに処理できます。
 * @param variantId - 商品バリアントID
 * @param properties - LINE ITEM PROPERTY
 * @param files - アップロードするファイル
 * @param additionalItems - 追加商品（オプション商品など）
 */
export const submitOrderWithFiles = async (
  variantId: string,
  properties: Record<string, string>,
  files: Record<string, File | null>,
  additionalItems: CartItem[] = []
) => {
  try {
    // オプション商品がある場合は先にJavaScript APIで追加
    if (additionalItems.length > 0) {
      await addItemsToCart(additionalItems);
    }

    // カートURLは全地域統一
    const cartAddUrl = '/cart/add';

    // HTMLフォームを動的に生成（メイン商品 + ファイル用）
    const form = document.createElement('form');
    form.action = cartAddUrl;
    form.method = 'POST';
    form.enctype = 'multipart/form-data';
    form.style.display = 'none';

    // メイン商品のバリアントID
    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = variantId;
    form.appendChild(variantInput);

    // LINE ITEM PROPERTIES
    Object.entries(properties).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${key}]`;
      input.value = value.toString();
      form.appendChild(input);
    });

    // ファイルアップロード
    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.name = `properties[${key}_file]`;
        
        // FileをDataTransferに設定
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInput.files = dataTransfer.files;
        
        form.appendChild(fileInput);
      }
    });

    // フォームをDOMに追加して送信
    document.body.appendChild(form);
    form.submit();
    
    // 送信後にフォームを削除
    setTimeout(() => {
      document.body.removeChild(form);
    }, 100);
    
  } catch (error) {
    throw error;
  }
};

/**
 * ファイルサイズとタイプの検証を行います。
 * @param file - 検証するファイル
 * @returns 検証結果
 */
export const validateFile = (file: File): { isValid: boolean; error?: string } => {
  const t = useTranslations();
  const maxSize = 20 * 1024 * 1024; // 20MB
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'application/illustrator'];
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.pdf', '.ai'];
  
  const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
  
  if (file.size > maxSize) {
    return {
      isValid: false,
      error: `ファイルサイズは20MB以下にしてください。現在のサイズ: ${(file.size / 1024 / 1024).toFixed(2)}MB`
    };
  }

  if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
    return {
      isValid: false,
      error: t.shopify_fileUploadError
    };
  }

  return { isValid: true };
};

/**
 * 下位互換性のため残している関数（警告付き）
 */
export const uploadFileToShopify = async (file: File): Promise<any> => {
  const validation = validateFile(file);
  if (!validation.isValid) {
    return { success: false, error: validation.error };
  }
  return { success: true, fileName: file.name };
};

export const getStoredFileData = (_fileId: string): any | null => {
  return null;
};

export const removeStoredFileData = (_fileId: string): void => {
}; 